import { Modal } from "@shopify/polaris"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { usePrompt } from "@/hooks/usePrompt"
import { getAppLink } from "@/utils/appBridge"

interface UnsavedChangesModalProps {
  when: boolean
  title?: string
  message?: string
  secondaryLabel?: string
  onConfirm: () => void
  allowedPaths?: string[]
  isTrackingPage?: boolean
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  when,
  title,
  message,
  secondaryLabel,
  onConfirm,
  allowedPaths = [],
  isTrackingPage = false,
}) => {
  const { t } = useTranslation(["settings", "common"])
  const [open, setOpen] = useState(false)
  const [blockRefCurrent, setBlockRefCurrent] = useState<{ action: string; location: { pathname: string } }>({
    action: "",
    location: { pathname: "" },
  })
  const navigate = useNavigate()
  const location = useLocation()

  // 判断目标路径是否在允许范围内
  const isPathAllowed = useCallback((targetPath: string) => {
    // 只有在 isTrackingPage=true 且设置了 allowedPaths 时才允许白名单路径
    if (!isTrackingPage || allowedPaths.length === 0) {
      return false
    }

    const normalizedPath = targetPath.includes(getAppLink())
      ? targetPath.replace(getAppLink(), "/")
      : targetPath

    const normalizedCurrent = location.pathname.includes(getAppLink())
      ? location.pathname.replace(getAppLink(), "/")
      : location.pathname

    // 检查是否都在同一个允许的根路径下
    return allowedPaths.some(allowedPath => {
      const normalizedAllowedPath = allowedPath.startsWith("/") ? allowedPath : `/${allowedPath}`
      const targetInPath = normalizedPath.startsWith(normalizedAllowedPath)
      const currentInPath = normalizedCurrent.startsWith(normalizedAllowedPath)

      // 只有当前路径和目标路径都在同一个允许的根路径下时才允许
      return targetInPath && currentInPath
    })
  }, [isTrackingPage, allowedPaths, location.pathname])

  usePrompt(([current]) => {
    // 设置当前导航信息
    setBlockRefCurrent({
      action: "push",
      location: { pathname: current.pathname || "" },
    })

    // 如果目标路径在允许范围内，不拦截
    if (current.pathname && isPathAllowed(current.pathname)) {
      // 在允许的路径内自由跳转，不触发拦截
      return true
    }

    // 如果有未保存的更改，显示弹窗并阻止导航
    if (when) {
      setOpen(true)
      return false
    }

    return true
  }, [when, isPathAllowed])

  const handleConfirm = () => {
    const { action, location } = blockRefCurrent || {}

    // 调用确认回调
    onConfirm()
    setOpen(false)

    // 执行导航
    if (action && action === "push") {
      setTimeout(() => {
        navigate(
          location.pathname.includes(getAppLink())
            ? location.pathname.replace(getAppLink(), "/")
            : location.pathname,
        )
      })
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={title || t("settings:EmailTemplateEditor.UnsavedModal.title")}
      primaryAction={{
        content: t("settings:EmailTemplateEditor.UnsavedModal.PrimaryButton"),
        onAction: handleConfirm,
        destructive: true,
      }}
      secondaryActions={[
        {
          content: secondaryLabel || t("common:Cancel"),
          onAction: () => setOpen(false),
        },
      ]}
    >
      <Modal.Section>
        {message || t("settings:EmailTemplateEditor.UnsavedModal.content")}
      </Modal.Section>
    </Modal>
  )
}

export default UnsavedChangesModal
